import { useRef, useState, useEffect } from "react";
import { Popover } from "@headlessui/react";
import { createPortal } from "react-dom";
import useClickOutside from "../../../hooks/useClickOutside";
import {
  CheckIcon,
  XMarkIcon,
  UserCircleIcon,
  CheckBadgeIcon,
  XCircleIcon,
  ArrowRightCircleIcon,
} from "@heroicons/react/24/solid";

interface InterviewSessionQuestion {
  id: string;
  candidateInterviewSlotId: string;
  assessmentQuestionId: string;
  assessmentQuestion: {
    id: string;
    text: string;
    type: string;
    time: number;
    tags: string;
    accuracy: number;
    parentQuestionId?: string | null;
    candidateAnswers: Array<{
      id: string;
      candidateId: string;
      accuracy: null | number;
      manualEvaluation: number;
      answer: null | string;
      time: null | number;
    }>;
  };
}

export interface InterviewSession {
  id: string;
  candidateId: string;
  assessmentQuestionnaireId: string;
  candidate: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  status: number;
  startTime: string;
  endTime: string;
  roleId: string;
  roleStatusId: string;
  meetingLink: string;
  interviewerEmail: string | null;
  interviewSessionQuestions: InterviewSessionQuestion[];
}

interface Props {
  interviewSession: InterviewSession | null;
  onClose: () => void;
}

const InterviewScorePanel = ({ interviewSession, onClose }: Props) => {
  const [manualScores, setManualScores] = useState<{ [key: string]: string }>({});
  const [editingScoreValues, setEditingScoreValues] = useState<{
    [key: string]: string;
  }>({});
  const [grades, setGrades] = useState<{ [key: string]: number }>({});
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const modalRef = useRef(null);

  useClickOutside(modalRef, () => {
    onClose();
  });

  useEffect(() => {
    if (!interviewSession) return;

    const initialGrades: { [key: string]: number } = {};
    const initialScores: { [key: string]: string } = {};

    interviewSession.interviewSessionQuestions.forEach((sessionQuestion) => {
      const candidateAnswers = sessionQuestion.assessmentQuestion.candidateAnswers;
      if (candidateAnswers && candidateAnswers.length > 0) {
        // Use the latest manual evaluation (last one in array)
        const manualEval = candidateAnswers[candidateAnswers.length - 1].manualEvaluation;
        const gradeMapping = { 0: 3, 1: 2, 2: 1 };
        initialGrades[sessionQuestion.id] = gradeMapping[manualEval as keyof typeof gradeMapping] || 0;

        const scoreMapping = { 0: "0", 1: "6", 2: "10" };
        initialScores[sessionQuestion.id] = scoreMapping[manualEval as keyof typeof scoreMapping] || "";
      }
    });

    setGrades(initialGrades);
    setManualScores(initialScores);
  }, [interviewSession]);

  const gradeToStatus = (grade: number): "correct" | "partial" | "wrong" | null => {
    if (grade === 1) return "correct";
    if (grade === 2) return "partial";
    if (grade === 3) return "wrong";
    return null;
  };

  const gradeColors = {
    E: {
      bg: "bg-emerald-500",
      hover: "hover:bg-emerald-600",
      text: "text-white",
      border: "border-emerald-600",
      ring: "ring-emerald-200",
      inactive:
        "border-emerald-500/40 hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-950/20",
    },
    P: {
      bg: "bg-blue-500",
      hover: "hover:bg-blue-600",
      text: "text-white",
      border: "border-blue-600",
      ring: "ring-blue-200",
      inactive:
        "border-blue-500/40 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/20",
    },
    F: {
      bg: "bg-red-500",
      hover: "hover:bg-red-600",
      text: "text-white",
      border: "border-red-600",
      ring: "ring-red-200",
      inactive:
        "border-red-500/40 hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-950/20",
    },
  };

  const handleGradeClick = (questionId: string, gradeValue: number) => {
    const currentGrade = grades[questionId];
    const isTogglingOff = currentGrade === gradeValue;
    setGrades({
      ...grades,
      [questionId]: isTogglingOff ? 0 : gradeValue,
    });
  };

  const handleScoreInputChange = (questionId: string, value: string) => {
    if (value === "" || (parseFloat(value) >= 0 && parseFloat(value) <= 10)) {
      setEditingScoreValues({ ...editingScoreValues, [questionId]: value });
    }
  };

  const handleSaveScore = (questionId: string) => {
    const scoreValue = editingScoreValues[questionId] ?? manualScores[questionId] ?? "";
    const numValue = scoreValue === "" ? undefined : parseFloat(scoreValue);

    if (
      scoreValue !== "" &&
      (isNaN(numValue!) || numValue! < 0 || numValue! > 10)
    ) {
      return;
    }

    setManualScores({ ...manualScores, [questionId]: scoreValue });
    const newEditingValues = { ...editingScoreValues };
    delete newEditingValues[questionId];
    setEditingScoreValues(newEditingValues);
  };

  const stripHtml = (html: string): string => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const StatusBadge = ({ result }: { result: "correct" | "partial" | "wrong" | null }) => {
    const styles: Record<string, string> = {
      correct: "bg-green-100 text-green-700",
      partial: "bg-amber-100 text-amber-700",
      wrong: "bg-red-100 text-red-700",
      null: "bg-gray-100 text-gray-700",
    };

    const icons: Record<string, React.ReactNode> = {
      correct: <CheckBadgeIcon className="w-4 h-4" />,
      partial: <CheckIcon className="w-4 h-4" />,
      wrong: <XCircleIcon className="w-4 h-4" />,
      null: <ArrowRightCircleIcon className="w-4 h-4" />,
    };

    const label: Record<string, string> = {
      correct: "Correct",
      partial: "Partially Correct",
      wrong: "Wrong",
      null: "Not Graded",
    };

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
          styles[result || "null"]
        }`}
      >
        {icons[result || "null"]}
        <span>{label[result || "null"]}</span>
      </span>
    );
  };

  const OverallSummary = () => {
    if (!interviewSession) return null;

    const graded = Object.values(grades).filter((g) => g > 0).length;
    const avgScore =
      Object.keys(manualScores).length > 0
        ? (
            Object.values(manualScores)
              .filter((s) => s !== "")
              .map((s) => parseFloat(s))
              .reduce((a, b) => a + b, 0) / Object.values(manualScores).length
          ).toFixed(1)
        : "N/A";

    const correctCount = Object.values(grades).filter((g) => g === 1).length;
    const partialCount = Object.values(grades).filter((g) => g === 2).length;
    const wrongCount = Object.values(grades).filter((g) => g === 3).length;

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md px-6 py-4 mb-4">
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
          Overall Summary
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <p className="text-sm md:text-lg font-bold text-green-600 dark:text-green-400">
              {correctCount}
            </p>
            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
              Correct
            </p>
          </div>

          <div className="text-center p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
            <p className="text-sm md:text-lg font-bold text-amber-600 dark:text-amber-400">
              {partialCount}
            </p>
            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
              Partially Correct
            </p>
          </div>

          <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <p className="text-sm md:text-lg font-bold text-red-600 dark:text-red-400">
              {wrongCount}
            </p>
            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
              Wrong
            </p>
          </div>

          <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-sm md:text-lg font-bold text-blue-600 dark:text-blue-400">
              {interviewSession.interviewSessionQuestions.length}
            </p>
            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
              Total
            </p>
          </div>
        </div>
      </div>
    );
  };

  const QuestionsTable = () => {
    if (!interviewSession) return null;

    const filteredQuestions = interviewSession.interviewSessionQuestions.filter((q) => {
      if (selectedStatus === "all") return true;
      const status = gradeToStatus(grades[q.id]);
      return status === selectedStatus;
    });

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden mb-4">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Questions Details
          </h3>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Filter:
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Questions</option>
              <option value="correct">Correct</option>
              <option value="partial">Partially Correct</option>
              <option value="wrong">Wrong</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <tr>
                <th className="px-3 py-2 text-left text-gray-700 dark:text-gray-300 font-semibold">
                  #
                </th>
                <th className="px-3 py-2 text-left text-gray-700 dark:text-gray-300 font-semibold">
                  Question
                </th>
                <th className="px-3 py-2 text-center text-gray-700 dark:text-gray-300 font-semibold">
                  Follow-up Of
                </th>
                <th className="px-3 py-2 text-center text-gray-700 dark:text-gray-300 font-semibold">
                  Grade
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredQuestions.map((q, idx) => {
                // Find parent question number if this is a follow-up
                const parentId = q.assessmentQuestion.parentQuestionId;
                let parentQuestionNumber: number | null = null;
                if (parentId) {
                  const parentIndex = interviewSession.interviewSessionQuestions.findIndex(
                    (iq) => iq.assessmentQuestion.id === parentId
                  );
                  if (parentIndex !== -1) {
                    parentQuestionNumber = parentIndex + 1;
                  }
                }

                return (
                  <tr
                    key={q.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400 font-medium">
                      {interviewSession.interviewSessionQuestions.findIndex((iq) => iq.id === q.id) + 1}
                    </td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-200 max-w-xs truncate">
                      {stripHtml(q.assessmentQuestion.text)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {parentQuestionNumber ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">
                          Q{parentQuestionNumber}
                        </span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <StatusBadge result={gradeToStatus(grades[q.id])} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredQuestions.length === 0 && (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No questions match the selected filter
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!interviewSession) return null;

  const modalContent = (
    <div className="fixed inset-0 flex justify-center z-50 h-screen backdrop-blur-sm">
      <div
        className="relative p-2 shadow-lg md:w-[80%] w-[95%] max-h-[90%] overflow-y-auto dark:bg-gray-800 bg-gray-50 top-14 rounded-md border border-gray-300 dark:border-gray-600"
        ref={modalRef}
      >
        <div className="mx-auto px-4 pt-0">
          {/* Header with Candidate Info */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md mb-4 flex justify-between items-center p-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                <UserCircleIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {interviewSession.candidate
                    ? `${interviewSession.candidate.firstName} ${interviewSession.candidate.lastName}`
                    : "Candidate"}
                </h2>
                {interviewSession.candidate && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {interviewSession.candidate.email}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <OverallSummary />
          {interviewSession.interviewSessionQuestions.length > 0 && <QuestionsTable />}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.getElementById("root") as HTMLElement);
};

export default InterviewScorePanel